import { Download } from "lucide-solid";
import { createSignal, onCleanup, onMount, Show } from "solid-js";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function runningAsApp(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: window-controls-overlay)").matches
  );
}

/** Bouton d'installation Chrome / Edge, y compris sur ordinateur. */
export function InstallAppButton() {
  const [promptEvent, setPromptEvent] = createSignal<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = createSignal(false);

  onMount(() => {
    if (runningAsApp()) setInstalled(true);
    const onPrompt = (event: Event) => {
      event.preventDefault();
      setPromptEvent(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setPromptEvent(null);
      setInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    onCleanup(() => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    });
  });

  const install = async () => {
    const event = promptEvent();
    if (!event) return;
    await event.prompt();
    setPromptEvent(null);
  };

  return (
    <Show when={promptEvent() && !installed()}>
      <button
        type="button"
        onClick={() => void install()}
        class="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2.5 text-[13px] font-semibold text-[var(--fg-strong)] shadow-[0_1px_2px_rgba(11,18,32,0.04)] transition hover:border-[var(--accent-alt)] hover:text-[var(--accent-alt)] sm:px-3"
        aria-label="Installer Scola sur cet appareil"
        title="Installer Scola sur cet ordinateur ou ce téléphone"
      >
        <Download size={15} stroke-width={1.75} />
        <span class="hidden sm:inline">Installer</span>
      </button>
    </Show>
  );
}
